import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Mail, Phone, MapPin, CalendarHeart } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { CONTACT, IMAGES, useContent } from "@/data/content";
import { useLang } from "@/i18n/LanguageContext";
import { useSeo } from "@/hooks/useSeo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-transparent border-b border-ink/25 py-3 text-base text-ink outline-none placeholder:text-soft/50 focus:border-clay transition-colors duration-300";

export default function Contact() {
  const [params] = useSearchParams();
  const { ui, offerings, thresholds } = useContent();
  const { lang } = useLang();
  const c = ui.contact;
  const TOPICS = [...offerings.map((o) => o.name), ...thresholds.map((t) => t.name), c.somethingElse];
  useSeo({ ...ui.seo.contact, image: IMAGES.hero });

  const [form, setForm] = useState({
    name: "", email: "", phone: "",
    topic: params.get("topic") || c.somethingElse,
    workshop_date: "",
    message: "",
    website: "", // honeypot — hidden from humans
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/enquiries`, { ...form, phone: form.phone || null, workshop_date: form.workshop_date || null, lang });
      toast.success(data.message);
      setSent(true);
    } catch {
      toast.error(c.error);
    } finally {
      setSending(false);
    }
  };

  return (
    <main className="bg-cream" data-testid="contact-page">
      <section className="mx-auto max-w-7xl px-6 lg:px-10 pt-40 pb-24 lg:pb-32">
        <Reveal>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-ink leading-[1.05] max-w-3xl">
            {c.title} <span className="italic text-clay">{c.accent}</span>
          </h1>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-soft">
            {c.intro}
          </p>
        </Reveal>

        <div className="mt-16 grid gap-16 lg:grid-cols-12">
          <Reveal className="lg:col-span-7">
            {sent ? (
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                className="border border-clay/30 bg-parchment p-10"
                data-testid="contact-success"
              >
                <p className="font-serif text-3xl text-ink">{c.successTitle} {form.name.split(" ")[0]}.</p>
                <p className="mt-4 text-base leading-relaxed text-soft">{c.successBody}</p>
              </motion.div>
            ) : (
              <form onSubmit={submit} className="space-y-8" data-testid="contact-form">
                <div className="grid gap-8 sm:grid-cols-2">
                  <div>
                    <label htmlFor="name" className="text-xs tracking-[0.25em] uppercase text-soft">{c.labels.name}</label>
                    <input id="name" required value={form.name} onChange={set("name")} placeholder={c.placeholders.name} className={inputCls} data-testid="contact-name-input" />
                  </div>
                  <div>
                    <label htmlFor="email" className="text-xs tracking-[0.25em] uppercase text-soft">{c.labels.email}</label>
                    <input id="email" type="email" required value={form.email} onChange={set("email")} placeholder={c.placeholders.email} className={inputCls} data-testid="contact-email-input" />
                  </div>
                </div>
                <div className="grid gap-8 sm:grid-cols-2">
                  <div>
                    <label htmlFor="phone" className="text-xs tracking-[0.25em] uppercase text-soft">{c.labels.phone} <span className="normal-case tracking-normal">{c.labels.optional}</span></label>
                    <input id="phone" value={form.phone} onChange={set("phone")} placeholder={c.placeholders.phone} className={inputCls} data-testid="contact-phone-input" />
                  </div>
                  <div>
                    <label htmlFor="topic" className="text-xs tracking-[0.25em] uppercase text-soft">{c.labels.topic}</label>
                    <select id="topic" value={form.topic} onChange={(e) => setForm((f) => ({ ...f, topic: e.target.value, workshop_date: "" }))} className={`${inputCls} cursor-pointer`} data-testid="contact-topic-select">
                      {TOPICS.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                </div>
                {(form.topic === "Active Birth Prenatal Workshop" || form.topic === "Birth Art") && (
                  <div>
                    <label htmlFor="workshop_date" className="text-xs tracking-[0.25em] uppercase text-soft">{c.workshopDate}</label>
                    <select id="workshop_date" value={form.workshop_date} onChange={set("workshop_date")} className={`${inputCls} cursor-pointer`} data-testid="contact-date-select">
                      <option value="">{c.workshopDateAny}</option>
                      {form.topic === "Active Birth Prenatal Workshop" ? (
                        <>
                          <option value="2026-10-31">{lang === "nl" ? "zaterdag 31 oktober 2026" : "Saturday 31 October 2026"}</option>
                        </>
                      ) : (
                        <>
                          <option value="2026-11-07">{lang === "nl" ? "zaterdag 7 november 2026" : "Saturday 7 November 2026"}</option>
                          <option value="2026-12-12">{lang === "nl" ? "zaterdag 12 december 2026" : "Saturday 12 December 2026"}</option>
                        </>
                      )}
                    </select>
                  </div>
                )}
                <div>
                  <label htmlFor="message" className="text-xs tracking-[0.25em] uppercase text-soft">{c.labels.message}</label>
                  <textarea id="message" required rows={5} value={form.message} onChange={set("message")}
                    className={`${inputCls} resize-none`} data-testid="contact-message-input" />
                </div>
                <div aria-hidden="true" className="absolute -left-[9999px] h-0 w-0 overflow-hidden">
                  <input type="text" name="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} data-testid="contact-honeypot" />
                </div>
                <button type="submit" disabled={sending} data-testid="contact-submit-button"
                  className="group inline-flex items-center gap-3 rounded-full bg-clay px-8 py-4 text-sm tracking-wide text-cream transition-colors duration-300 hover:bg-rust disabled:opacity-50">
                  {sending ? c.sending : c.submit}
                  <ArrowRight size={16} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
                </button>
              </form>
            )}
          </Reveal>

          <Reveal delay={0.15} className="lg:col-span-4 lg:col-start-9">
            <div className="space-y-10">
              <a href={CONTACT.introCallUrl} target="_blank" rel="noopener noreferrer" data-testid="contact-book-direct"
                className="group block border border-clay/40 bg-parchment p-8 transition-colors duration-300 hover:border-clay">
                <CalendarHeart size={22} strokeWidth={1.25} className="text-clay" />
                <p className="mt-4 font-serif text-2xl text-ink">{c.bookTitle}</p>
                <p className="mt-2 text-sm leading-relaxed text-soft">{c.bookBody}</p>
                <span className="mt-4 inline-flex items-center gap-2 text-sm text-clay">
                  {c.bookCta} <ArrowUpRight size={15} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </span>
              </a>
              <ul className="space-y-5 text-sm" data-testid="contact-details">
                <li className="flex items-start gap-4"><Mail size={18} strokeWidth={1.25} className="mt-0.5 text-clay" /><a href={`mailto:${CONTACT.email}`} className="text-ink hover:text-clay transition-colors" data-testid="contact-email-link">{CONTACT.email}</a></li>
                <li className="flex items-start gap-4"><Phone size={18} strokeWidth={1.25} className="mt-0.5 text-clay" /><a href={`tel:${CONTACT.phoneHref}`} className="text-ink hover:text-clay transition-colors" data-testid="contact-phone-link">{CONTACT.phone}</a></li>
                <li className="flex items-start gap-4"><MapPin size={18} strokeWidth={1.25} className="mt-0.5 text-clay" /><span className="text-ink/80" data-testid="contact-location"><span className="mb-1 block font-medium text-ink">{c.whereTitle}</span>{c.whereBody}</span></li>
              </ul>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
